import React, { createContext, useEffect, useMemo, useState } from 'react'
import { useRentRollRentalDetailsQuery, useUpup_LiabilityTypesQuery } from 'graphql/generated'
import type { RentRollRentalDetailsQuery, Upup_LiabilityTypesQuery } from 'graphql/generated'
import { Loading } from 'ui'

type RentalDetailsContextType = {
  rentalData: RentRollRentalDetailsQuery['rentals_by_pk']
  liabilityTypes: Upup_LiabilityTypesQuery['liability_types']
  refreshRentalDetails: () => void
}

type RentRollRentalDetails = NonNullable<RentRollRentalDetailsQuery['rentals_by_pk']>

const isRentalDetailsReady = (
  rental: RentRollRentalDetailsQuery['rentals_by_pk']
): rental is RentRollRentalDetails =>
  Boolean(
    rental &&
      rental.property &&
      Array.isArray(rental.property.llc_properties) &&
      Array.isArray(rental.rahWithRenewal) &&
      Array.isArray(rental.pending_evictions) &&
      Array.isArray(rental.rental_agreement_histories) &&
      Array.isArray(rental.collections) &&
      Array.isArray(rental.rental_users) &&
      Array.isArray(rental.rental_occupants) &&
      rental.rental_application &&
      Array.isArray(rental.rental_application.rental_application_occupants) &&
      (!rental.portfolio_home ||
        (rental.portfolio_home.home?.address &&
          Array.isArray(rental.portfolio_home.portfolio_home_dispositions))) &&
      (!rental.cashout || rental.cashout.user)
  )

export const RentalDetailsContext = createContext<RentalDetailsContextType>({
  rentalData: null,
  liabilityTypes: [],
  refreshRentalDetails: () => {}
})

export const RentalDetailsContextProvider: React.FC<
  React.PropsWithChildren<{ rentalId: string }>
> = ({ rentalId, children }) => {
  const queryContext = useMemo(() => ({ suspense: false }), [])
  const [readyRental, setReadyRental] = useState<RentRollRentalDetails | null>(null)
  const [{ data: liabilityTypesData }] = useUpup_LiabilityTypesQuery({ context: queryContext })
  const [{ data: rentalDetailsData, fetching }, refetchRentalDetails] =
    useRentRollRentalDetailsQuery({
      variables: { rentalId },
      requestPolicy: 'cache-and-network',
      context: queryContext
    })

  const refreshRentalDetails = () => {
    refetchRentalDetails({ requestPolicy: 'network-only' })
  }

  const rental = rentalDetailsData?.rentals_by_pk
  const isMissingRental = !!rentalDetailsData && rentalDetailsData.rentals_by_pk === null
  const currentReadyRental = isRentalDetailsReady(rental) && rental.id === rentalId ? rental : null

  useEffect(() => {
    setReadyRental(null)
  }, [rentalId])

  useEffect(() => {
    if (currentReadyRental) {
      setReadyRental(currentReadyRental)
    }
  }, [currentReadyRental])

  // Child detail panels write narrower rental shapes into graphcache; keep the last complete
  // details snapshot so those background writes cannot remount the whole page into Loading.
  const rentalForContext = currentReadyRental ?? (readyRental?.id === rentalId ? readyRental : null)

  if (!liabilityTypesData || (fetching && !rentalForContext && !isMissingRental)) {
    return <Loading />
  }

  if (!rentalForContext && !isMissingRental) {
    return <Loading />
  }

  return (
    <RentalDetailsContext.Provider
      value={{
        liabilityTypes: liabilityTypesData.liability_types,
        rentalData: isMissingRental ? null : rentalForContext,
        refreshRentalDetails
      }}
    >
      {children}
    </RentalDetailsContext.Provider>
  )
}
